import React, { useEffect, useState } from 'react'
import moment from 'moment'
import * as yup from 'yup'
import {
  Chip,
  Checkbox,
  Grid,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  TextField as MuiTextField,
} from '@material-ui/core'
import {
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
} from '@material-ui/icons'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { useDebouncedCallback } from 'use-debounce'

import { Address, Place } from 'src/api/models'
import { Paginator } from 'src/components/Table'
import { Field } from './form'

const filter = createFilterOptions();

yup.addMethod<yup.StringSchema>(yup.string, 'client', function() {
  return this.matches(/ac_[a-zA-Z0-9]{22}/).label('Client')
})
yup.addMethod<yup.StringSchema>(yup.string, 'asn', function() {
  return this.matches(/as_[a-zA-Z0-9]{22}/).label('ASN')
})
yup.addMethod<yup.StringSchema>(yup.string, 'carrier', function() {
  return this.matches(/cr_[a-zA-Z0-9]{22}/).label('Carrier')
})
yup.addMethod<yup.StringSchema>(yup.string, 'dock', function() {
  return this.matches(/dd_[a-zA-Z0-9]{22}/).label('Dock')
})
yup.addMethod<yup.StringSchema>(yup.string, 'driver', function() {
  return this.matches(/us_[a-zA-Z0-9]{22}/).label('Driver')
})
yup.addMethod<yup.StringSchema>(yup.string, 'facility', function() {
  return this.matches(/fa_[a-zA-Z0-9]{22}/).label('Facility')
})
yup.addMethod<yup.StringSchema>(yup.string, 'place', function() {
  return this.matches(/pl_[a-zA-Z0-9]{22}/).label('Place')
})
yup.addMethod<yup.StringSchema>(yup.string, 'sku', function() {
  return this.matches(/sk_[a-zA-Z0-9]{22}/).label('SKU')
})
yup.addMethod<yup.StringSchema>(yup.string, 'truckload', function() {
  return this.matches(/tl_[a-zA-Z0-9]{22}/).label('Truckload')
})
yup.addMethod<yup.ObjectSchema>(yup.object, 'hoursOfOperation', function() {
  return this.shape({
    open: yup.number().label('Open Time'),
    close: yup.number().label('Close Time'),
  }).test('openBeforeClose', 'Open time must be before close time', function(value) {
    const { open, close } = value
    return open === undefined || close === undefined || open <= close
  })
})

yup.addMethod<yup.StringSchema>(yup.string, 'oneRequired', function(fields: object) {
  const names = Object.values(fields)
  const fieldNames = Object.keys(fields)
  const fieldsStr = names.slice(0, -1).join(', ') + ', or ' + names.slice(-1)
  return this.test('oneOfRequired', `One of ${fieldsStr} must be provided`, function(value) {
    const values = fieldNames.map((name) => {
      return this.parent[name]
    })
    if (values.every((value) => !value)) return value != null
    return true
  })
})

interface SelectFieldProps {
  name: string
  label: string
  options: object
  defaultValue?: any
  allowNull?: boolean
  disabled?: boolean
  onSelected?: (obj: any) => void
}

export const MapSelectField = (props: SelectFieldProps) => {
  const { name, label, options, defaultValue, allowNull = false, disabled, onSelected } = props

  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      getSubmitValue={(value) => value}
      renderField={({ name, value, setValue, error }) => (
        <FormControl fullWidth>
          <InputLabel id={name}>{label}</InputLabel>
          <Select
            disabled={disabled}
            id={name}
            name={name}
            label={label}
            error={!!error}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setValue(name, event.target.value as string)
              onSelected?.(event.target.value)
            }}
            value={value || ''}
          >
            {allowNull && <MenuItem value={''}>-</MenuItem>}
            {Object.entries(options).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error={!!error}>{error}</FormHelperText>
        </FormControl>
      )}
    />
  )
}

interface DialogProps {
  open: boolean
  handleClose: () => void
  onSave?: (obj: any) => void
}

interface MultiPlaceSelectFieldProps {
  name: string
  label: string
  paginator: Paginator
  facilityPaginator: Paginator
  renderSelected: React.FC<RenderProps>
  defaultValue?: any
  disabled?: boolean
  onSelected?: (obj: any) => void
  CreateDialog?: React.FC<DialogProps>
  createOnSave?: (obj: any) => void
}

export const MultiPlaceSelectField = (props: MultiPlaceSelectFieldProps) => {
  const {
    name,
    label,
    paginator,
    facilityPaginator,
    defaultValue,
    disabled,
    onSelected,
    CreateDialog,
    createOnSave,
    renderSelected,
  } = props
  const [open, setOpen] = useState(false)
  const [path, setPath] = useState<Place[]>([])

  const dePatch = useDebouncedCallback(paginator.patchFilters, 200)
  const patchFilters = paginator.patchFilters

  useEffect(() => {
    if (path.length) {
      if (path.length === 1) {
        patchFilters({ facilities: path[0].guid, parents: null, noparent: true, page_size: 100 })
      } else {
        patchFilters({
          facilities: null,
          parents: path[path.length - 1].guid,
          noparent: null,
          page_size: 500,
        })
      }
    }
  }, [path, patchFilters])

  const pg = path.length ? paginator : facilityPaginator

  return (
    <>
      {open && !!CreateDialog && (
        <CreateDialog open={open} handleClose={() => setOpen(false)} onSave={createOnSave} />
      )}
      <Field
        name={name}
        defaultValue={defaultValue}
        getSubmitValue={(value) => value.map((obj: any) => obj.guid)}
        renderField={({ name, value, setValue, error }) => (
          <Grid container>
            <Grid item xs={CreateDialog ? 11 : 12}>
              <Autocomplete
                disabled={disabled}
                id={name}
                options={pg.data.results}
                getOptionLabel={(obj) => obj.name}
                getOptionSelected={(option, value) => option.guid === value?.guid}
                filterOptions={(option) => option}
                onChange={(_: any, newValue: any | null) => {
                  setPath([...path, newValue])
                }}
                value={null}
                renderInput={(params) => (
                  <Grid container spacing={2} alignItems="center">
                    {path.map((obj, index) => (
                      <React.Fragment key={obj.guid}>
                        <Grid item>
                          <ChevronRightIcon />
                        </Grid>
                        <Grid item>
                          <Chip
                            label={obj.name}
                            onDelete={() => {
                              path.splice(index)
                              setPath([...path])
                            }}
                          />
                        </Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={4}>
                      <MuiTextField
                        {...params}
                        name={name}
                        label={pg.loading ? 'Loading...' : label}
                        error={!!error}
                        helperText={error}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          dePatch.callback({ search: event.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    {path.length > 1 && (
                      <Grid item>
                        <IconButton
                          color="primary"
                          onClick={() => {
                            const place = path[path.length - 1]
                            const newValueList = value ? [...value, place] : [place]
                            setValue(name, newValueList)
                            setPath([])
                            onSelected?.(newValueList)
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    )}
                    {pg.loading && <LinearProgress />}
                  </Grid>
                )}
              />
            </Grid>
            {!!CreateDialog && (
              <Grid item xs={1}>
                <IconButton color="primary" onClick={() => setOpen(true)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container>
                {value?.map((obj: any) => {
                  if (obj) {
                    return (
                      <React.Fragment key={obj.guid}>
                        <Grid item>{renderSelected({ obj })}</Grid>
                        <Grid item>
                          <IconButton
                            color="primary"
                            disabled={disabled}
                            onClick={() => {
                              const newValueList = value.filter((v: any) => v.guid !== obj.guid)
                              setValue(name, newValueList)
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </React.Fragment>
                    )
                  }
                })}
              </Grid>
            </Grid>
          </Grid>
        )}
      />
    </>
  )
}

interface QuerySelectFieldProps {
  name: string
  label: string
  paginator: Paginator
  defaultValue?: any
  disabled?: boolean
  getOptionLabel?: (obj: any) => any
  renderOption?: (obj: any) => any
  onSelected?: (obj: any) => void
  CreateDialog?: React.FC<DialogProps>
  createOnSave?: (obj: any) => void
}

export const QuerySelectField = (props: QuerySelectFieldProps) => {
  const {
    name,
    label,
    paginator,
    defaultValue,
    disabled,
    getOptionLabel = (obj: any) => obj?.name,
    renderOption,
    onSelected,
    CreateDialog,
    createOnSave,
  } = props
  const [open, setOpen] = useState(false)

  const dePatch = useDebouncedCallback(paginator.patchFilters, 200)
  return (
    <>
      <Field
        name={name}
        defaultValue={defaultValue}
        getSubmitValue={(obj) => obj?.guid}
        renderField={({ name, value, setValue, error }) => (
          <>
            {open && !!CreateDialog && (
              <CreateDialog
                open={open}
                handleClose={() => setOpen(false)}
                onSave={(obj) => {
                  setValue(name, obj)
                  createOnSave?.(obj)
                }}
              />
            )}
            <Grid container>
              <Grid item xs={12}>
                <Autocomplete
                  disabled={disabled}
                  id={name}
                  options={paginator.data.results}
                  getOptionLabel={getOptionLabel}
                  getOptionSelected={(option, value) => option?.guid === value?.guid}
                  renderOption={renderOption}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    if (CreateDialog) {
                      filtered.push({name: `Create new ${label}`, action: "createNew"})
                    }
                    return filtered
                  }}
                  onChange={(_: any, newValue: any | null) => {
                    if (!!CreateDialog && typeof newValue === 'string') {
                      setOpen(true)
                    } else if (!!CreateDialog && newValue && newValue.action === "createNew") {
                      setOpen(true)
                    } else {
                      setValue(name, newValue)
                      onSelected?.(newValue)
                    }
                  }}
                  value={value}
                  renderInput={(params) => (
                    <>
                      <MuiTextField
                        {...params}
                        name={name}
                        label={paginator.loading ? 'Loading...' : label}
                        error={!!error}
                        helperText={error}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          dePatch.callback({ search: event.target.value })
                        }
                      />
                      {paginator.loading && <LinearProgress />}
                    </>
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}
      />
    </>
  )
}

interface RenderProps {
  obj: any
}

export const GenerateChipDisplay = (fields: string[]) => {
  const ChipDisplay = (props: RenderProps) => {
    const { obj } = props
    const text = fields.map((field: string) => obj[field]).join(' ')
    return <Chip label={text} />
  }

  return ChipDisplay
}

interface QueryMultiSelectFieldProps {
  name: string
  label: string
  paginator: Paginator
  renderSelected: React.FC<RenderProps>
  defaultValue?: any
  getOptionLabel?: (obj: any) => any
  disabled?: boolean
  onSelected?: (obj: any) => void
  CreateDialog?: React.FC<DialogProps>
  createOnSave?: (obj: any) => void
}

export const QueryMultiSelectField = (props: QueryMultiSelectFieldProps) => {
  const {
    name,
    label,
    paginator,
    defaultValue,
    getOptionLabel = (obj: any) => obj?.name,
    disabled,
    onSelected,
    CreateDialog,
    createOnSave,
    renderSelected,
  } = props
  const [open, setOpen] = useState(false)

  const dePatch = useDebouncedCallback(paginator.patchFilters, 200)
  return (
    <>
      {open && !!CreateDialog && (
        <CreateDialog open={open} handleClose={() => setOpen(false)} onSave={createOnSave} />
      )}
      <Field
        name={name}
        defaultValue={defaultValue}
        getSubmitValue={(value) => value.map((obj: any) => obj.guid)}
        renderField={({ name, value, setValue, error }) => (
          <Grid container>
            <Grid item xs={CreateDialog ? 11 : 12}>
              <Autocomplete
                disabled={disabled}
                id={name}
                options={paginator.data.results}
                getOptionLabel={getOptionLabel}
                getOptionSelected={(option, value) => option.guid === value?.guid}
                filterOptions={(option) => option}
                onChange={(_: any, newValue: any | null) => {
                  const newValueList = value ? [...value, newValue] : [newValue]
                  setValue(name, newValueList)
                  onSelected?.(newValueList)
                }}
                value={null}
                renderInput={(params) => (
                  <>
                    <MuiTextField
                      {...params}
                      name={name}
                      label={paginator.loading ? 'Loading...' : label}
                      error={!!error}
                      helperText={error}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dePatch.callback({ search: event.target.value })
                      }
                    />
                    {paginator.loading && <LinearProgress />}
                  </>
                )}
              />
            </Grid>
            {!!CreateDialog && (
              <Grid item xs={1}>
                <IconButton color="primary" onClick={() => setOpen(true)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container>
                {value?.map((obj: any) => {
                  if (obj) {
                    return (
                      <React.Fragment key={obj.guid}>
                        <Grid item>{renderSelected({ obj })}</Grid>
                        <Grid item>
                          <IconButton
                            color="primary"
                            onClick={() => {
                              const newValueList = value.filter((v: any) => v.guid !== obj.guid)
                              setValue(name, newValueList)
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </React.Fragment>
                    )
                  }
                })}
              </Grid>
            </Grid>
          </Grid>
        )}
      />
    </>
  )
}

interface TextFieldProps {
  name: string
  label: string
  defaultValue?: any
  disabled?: boolean
}

export const TextField = (props: TextFieldProps) => {
  const { name, label, defaultValue, disabled } = props
  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      renderField={({ name, value, setValue, error }) => (
        <MuiTextField
          disabled={disabled}
          id={name}
          name={name}
          variant="standard"
          label={label}
          error={!!error}
          helperText={error}
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(name, e.target.value)}
          value={value || ''}
        />
      )}
    />
  )
}

interface TextareaFieldProps {
  name: string
  label: string
  defaultValue?: any
  disabled?: boolean
}

export const TextareaField = (props: TextareaFieldProps) => {
  const { name, label, defaultValue, disabled } = props
  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      renderField={({ name, value, setValue, error }) => (
        <MuiTextField
          disabled={disabled}
          id={name}
          name={name}
          variant="standard"
          multiline
          rows={4}
          label={label}
          error={!!error}
          helperText={error}
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(name, e.target.value)}
          value={value || ''}
        />
      )}
    />
  )
}

interface NumberFieldProps {
  name: string
  label: string
  defaultValue?: any
  disabled?: boolean
}

export const NumberField = (props: NumberFieldProps) => {
  const { name, label, defaultValue, disabled } = props
  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      renderField={({ name, value, setValue, error }) => (
        <MuiTextField
          type="number"
          disabled={disabled}
          id={name}
          name={name}
          variant="standard"
          label={label}
          error={!!error}
          helperText={error}
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(name, e.target.value)}
          value={value || ''}
        />
      )}
    />
  )
}

interface BooleanFieldProps {
  name: string
  label: string
  defaultValue?: any
  disabled?: boolean
}

export const BooleanField = (props: BooleanFieldProps) => {
  const { name, label, defaultValue, disabled } = props
  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      renderField={({ name, value, setValue }) => (
        <FormControlLabel
          control={
            <Checkbox
              disabled={disabled}
              id={name}
              name={name}
              checked={!!value}
              onChange={() => setValue(name, !value)}
              value={value || ''}
            />
          }
          label={label}
        />
      )}
    />
  )
}

const minutesToTime = (minute: number) => {
  const hour = Math.floor(minute / 60).toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })
  const min = (minute % 60).toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })
  return `${hour}:${min}`
}

const timeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':')
  return parseInt(hours) * 60 + parseInt(minutes)
}

interface HoursOfOperationFieldProps {
  name: string
  label: string
  defaultValue?: any
  disabled?: boolean
}

export const HoursOfOperationField = (props: HoursOfOperationFieldProps) => {
  const { name, label, defaultValue, disabled } = props
  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      renderField={({ name, value, setValue, error }) => {
        const enabled = !!value
        const open = !!value && value.open !== value.close
        return (
          <Grid container spacing={2}>
            <Grid item xs={1}>
              {label}
            </Grid>
            <Grid item xs={1}>
              <Switch
                checked={enabled}
                onChange={() =>
                  enabled ? setValue(name, undefined) : setValue(name, { open: 540, close: 1020 })
                }
                color="primary"
                name="checkedB"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            </Grid>
            <Grid item xs={2}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!enabled}
                    checked={open}
                    onChange={() =>
                      value.open === value.close
                        ? setValue(name, { open: 540, close: 1020 })
                        : setValue(name, { open: 0, close: 0 })
                    }
                    color="primary"
                    name="checkedB"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                }
                label={open ? 'open' : 'closed'}
              />
            </Grid>
            <Grid item xs={4}>
              <MuiTextField
                type="time"
                disabled={!enabled || disabled || !open}
                id={name}
                name={name}
                variant="standard"
                label="Open"
                error={!!error}
                helperText={error}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(name, { ...value, open: timeToMinutes(e.target.value) })
                }
                value={value?.open ? minutesToTime(value.open) : ''}
              />
            </Grid>
            <Grid item xs={4}>
              <MuiTextField
                type="time"
                disabled={!enabled || disabled || !open}
                id={name}
                name={name}
                variant="standard"
                label="Close"
                error={!!error}
                helperText={error}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(name, { ...value, close: timeToMinutes(e.target.value) })
                }
                value={value?.close ? minutesToTime(value.close) : ''}
              />
            </Grid>
          </Grid>
        )
      }}
    />
  )
}

interface DateTimeFieldProps {
  name: string
  label: string
  defaultValue?: moment.Moment
  disabled?: boolean
  minTime?: string
  maxTime?: string
  onChange?: (value: moment.Moment) => void
}

export const DateTimeField = (props: DateTimeFieldProps) => {
  const { name, label, defaultValue, disabled, minTime, maxTime, onChange } = props
  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      renderField={({ name, value, setValue, error }) => (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MuiTextField
              type="date"
              disabled={disabled}
              id={name}
              name={name}
              variant="standard"
              label={label}
              error={!!error}
              helperText={error}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const time = value ? value.format('HH:mm:ss') : '00:00:00'
                const newValue = moment(`${e.target.value} ${time}`)
                setValue(name, newValue)
                onChange?.(newValue)
              }}
              value={value ? value.format('YYYY-MM-DD') : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <MuiTextField
              type="time"
              disabled={disabled}
              id={name}
              name={name}
              variant="standard"
              label={label}
              error={!!error}
              helperText={error}
              fullWidth
              inputProps={{ min: minTime, max: maxTime }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const date = value ? value.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
                const newValue = moment(`${date} ${e.target.value}`)
                setValue(name, newValue)
                onChange?.(newValue)
              }}
              value={value ? value.format('HH:mm:ss') : ''}
            />
          </Grid>
        </Grid>
      )}
    />
  )
}

interface DateFieldProps {
  name: string
  label: string
  defaultValue?: moment.Moment
  disabled?: boolean
}

export const DateField = (props: DateFieldProps) => {
  const { name, label, defaultValue, disabled } = props
  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      getSubmitValue={(value) => (value ? value.format('YYYY-MM-DD') : undefined)}
      renderField={({ name, value, setValue, error }) => (
        <MuiTextField
          type="date"
          disabled={disabled}
          id={name}
          name={name}
          variant="standard"
          label={label}
          error={!!error}
          helperText={error}
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(name, moment(e.target.value))
          }}
          value={value ? value.format('YYYY-MM-DD') : ''}
        />
      )}
    />
  )
}

interface AddressFieldProps {
  name: string
  defaultValue?: Address
  disabled?: boolean
}

export const AddressField = (props: AddressFieldProps) => {
  const { name, defaultValue, disabled } = props
  return (
    <Field
      name={name}
      renderField={() => (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="company"
                label="Company"
                defaultValue={defaultValue?.company}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="consignee"
                label="Consignee"
                defaultValue={defaultValue?.consignee}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="phone"
                label="Phone Number"
                defaultValue={defaultValue?.phone}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="address1"
                label="Address 1"
                defaultValue={defaultValue?.address1}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="address2"
                label="Address 2"
                defaultValue={defaultValue?.address2}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="address3"
                label="Address 3"
                defaultValue={defaultValue?.address3}
                disabled={disabled}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                name="city"
                label="City"
                defaultValue={defaultValue?.city}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="state"
                label="State"
                defaultValue={defaultValue?.state}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="postal_code"
                label="Postal Code"
                defaultValue={defaultValue?.postalCode}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </>
      )}
    />
  )
}
